import React, { useEffect, useState } from 'react';
import NumberInputWithUnit from '../../../../../../atomic/inputs/numberInputWithUnit/NumberInputWithUnit';
import InfoIconComponent from '../../InfoIcon';
import style from './ExitTacticModules.module.css';
import PropTypes from 'prop-types';
import RatchetBracketDocs from '../../../../documentation/RatchetBracketDocs';
import { UnitIndicator } from '../../../../../../atomic/formElements/unitIndicator/UnitIndicator';
import { colors } from '../../../../../../../colorVars';

function RatchetBracketModule({ state }) {
  const [ratchetBracket, dispatchRatchetBracket, , setRatchetBracketEnabled] =
    state;
  const perBracketLabels = {
    PROFIT: 'Num. of Brackets',
    QUANTITY: 'Qty. per Bracket',
    PERCENT: '% per Bracket',
    DEFAULT: 'Qty. Basis Value',
  };

  const isOcoMode = ratchetBracket.mode === 'OCO_ANCHORED';

  const [targetPerBracketDisplay, setTargetPerBracketDisplay] = useState(0);
  const [stopPerBracketDisplay, setStopPerBracketDisplay] = useState(0);
  const [ocoStopPriceDisplay, setOcoStopPriceDisplay] = useState(0);
  const [ocoTargetPriceDisplay, setOcoTargetPriceDisplay] = useState(0);
  const [ocoNumberOfTargetsDisplay, setOcoNumberOfTargetsDisplay] = useState(1);
  const [qtyBasisValuePerBracketDisplay, setQtyBasisValuePerBracketDisplay] =
    useState(0);
  const [trailQtyBasisValueDisplay, setTrailQtyBasisValueDisplay] = useState(0);

  useEffect(() => {
    setRatchetBracketEnabled(true);
  }, []);

  useEffect(() => {
    setTargetPerBracketDisplay(ratchetBracket.targetPerBracket ?? 0);
    setStopPerBracketDisplay(ratchetBracket.stopPerBracket ?? 0);
    setOcoStopPriceDisplay(ratchetBracket.ocoStopPrice ?? 0);
    setOcoTargetPriceDisplay(ratchetBracket.ocoTargetPrice ?? 0);
    setOcoNumberOfTargetsDisplay(ratchetBracket.ocoNumberOfTargets ?? 1);
    setQtyBasisValuePerBracketDisplay(
      (ratchetBracket.qtyBasis === 'QUANTITY' ||
      ratchetBracket.qtyBasis === 'PROFIT'
        ? ratchetBracket.qtyBasisValuePerBracket
        : ratchetBracket.qtyBasisValuePerBracket * 100) ?? 0
    );
    setTrailQtyBasisValueDisplay(
      (ratchetBracket.qtyBasis === 'QUANTITY'
        ? ratchetBracket.trailQtyBasisValue
        : ratchetBracket.trailQtyBasisValue * 100) ?? 0
    );
  }, [state]);

  const handleModeChange = ({ target }) => {
    const newMode = target.value;
    dispatchRatchetBracket({ mode: newMode });
    if (newMode === 'OCO_ANCHORED') {
      dispatchRatchetBracket({ ocoStopPrice: 0, ocoTargetPrice: 0, ocoNumberOfTargets: 1 });
      if (ratchetBracket.qtyBasis === 'PROFIT') {
        dispatchRatchetBracket({ qtyBasis: 'QUANTITY' });
      }
    } else {
      dispatchRatchetBracket({ ocoStopPrice: null, ocoTargetPrice: null, ocoNumberOfTargets: null });
    }
  };

  return (
    <div className={style.moduleStyle}>
      <span className={style.tacticName}>
        <InfoIconComponent docs={RatchetBracketDocs} /> Ratchet Bracket
      </span>
      <div className={style.inputs}>
        <div className={style.inputAndLabel}>
          <p>Mode</p>
          <select value={ratchetBracket.mode} onChange={handleModeChange}>
            <option value="EQUAL_INTERVAL">Equal Interval</option>
            <option value="OCO_ANCHORED">OCO Anchored</option>
          </select>
        </div>

        {isOcoMode ? (
          <>
            <div className={style.inputAndLabel}>
              <p>Stop Price</p>
              <NumberInputWithUnit
                inputWidth="5em"
                direction="left"
                type="number"
                value={ocoStopPriceDisplay}
                setValue={setOcoStopPriceDisplay}
                onBlur={(value) => dispatchRatchetBracket({ ocoStopPrice: value })}
              >
                $
              </NumberInputWithUnit>
            </div>

            <div className={style.inputAndLabel}>
              <p>Target Price</p>
              <NumberInputWithUnit
                inputWidth="5em"
                direction="left"
                type="number"
                value={ocoTargetPriceDisplay}
                setValue={setOcoTargetPriceDisplay}
                onBlur={(value) => dispatchRatchetBracket({ ocoTargetPrice: value })}
              >
                $
              </NumberInputWithUnit>
            </div>

            <div className={style.inputAndLabel}>
              <p>Num. of Targets</p>
              <NumberInputWithUnit
                inputWidth="5em"
                direction="left"
                type="number"
                value={ocoNumberOfTargetsDisplay}
                setValue={setOcoNumberOfTargetsDisplay}
                onBlur={(value) =>
                  dispatchRatchetBracket({
                    ocoNumberOfTargets: Math.max(1, Math.round(Number(value))),
                  })
                }
              >
                #
              </NumberInputWithUnit>
            </div>
          </>
        ) : (
          <>
            <div className={style.inputAndLabel}>
              <p>
                {ratchetBracket.qtyBasis === 'PROFIT'
                  ? 'Target Profit'
                  : 'Target per Bracket'}
              </p>
              <NumberInputWithUnit
                inputWidth="5em"
                direction="left"
                type="number"
                value={targetPerBracketDisplay}
                setValue={setTargetPerBracketDisplay}
                onBlur={(value) =>
                  dispatchRatchetBracket({ targetPerBracket: value })
                }
              >
                $
              </NumberInputWithUnit>
            </div>

            <div className={style.inputAndLabel}>
              <p>
                {ratchetBracket.qtyBasis === 'PROFIT' ? 'Stop Loss' : 'Stop per Bracket'}
              </p>
              <NumberInputWithUnit
                inputWidth="5em"
                direction="left"
                type="number"
                value={stopPerBracketDisplay}
                setValue={setStopPerBracketDisplay}
                onBlur={(value) =>
                  dispatchRatchetBracket({ stopPerBracket: value })
                }
              >
                $
              </NumberInputWithUnit>
            </div>
          </>
        )}

        <div className={style.inputAndLabel}>
          <p className={style.labelAndIndicator}>
            {' '}
            <UnitIndicator
              margin="0 3px 0 0"
              height={12}
              width={12}
              color={
                ratchetBracket.qtyBasis === 'PROFIT'
                  ? colors.green
                  : ratchetBracket.qtyBasis === 'PERCENT'
                  ? colors.orange
                  : colors.blue
              }
            />
            Qty. Basis
          </p>
          <select
            value={ratchetBracket.qtyBasis}
            onChange={({ target }) => {
              dispatchRatchetBracket({ qtyBasis: target.value });
              dispatchRatchetBracket({ qtyBasisValuePerBracket: 0 });
              dispatchRatchetBracket({ trailQtyBasisValue: 0 });
            }}
          >
            {!isOcoMode && <option value="PROFIT">Profit</option>}
            <option value="QUANTITY">Quantity</option>
            <option value="PERCENT">Percentage</option>
          </select>
        </div>

        <div className={style.inputAndLabel}>
          <p>
            {perBracketLabels[ratchetBracket.qtyBasis] ||
              perBracketLabels.DEFAULT}
          </p>
          <NumberInputWithUnit
            direction={ratchetBracket.qtyBasis === 'PERCENT' ? 'right' : 'left'}
            type="number"
            value={qtyBasisValuePerBracketDisplay}
            setValue={setQtyBasisValuePerBracketDisplay}
            onBlur={(value) =>
              dispatchRatchetBracket({
                qtyBasisValuePerBracket:
                  ratchetBracket.qtyBasis === 'PERCENT'
                    ? Number(value) / 100
                    : value,
              })
            }
          >
            {ratchetBracket.qtyBasis === 'PERCENT' ? '%' : '#'}
          </NumberInputWithUnit>
        </div>

        <div className={style.inputAndLabel}>
          <p>
            {ratchetBracket.qtyBasis === 'QUANTITY' ? 'Trail Qty.' : 'Trail %'}
          </p>
          <NumberInputWithUnit
            direction={
              ratchetBracket.qtyBasis === 'QUANTITY' ? 'left' : 'right'
            }
            type="number"
            value={trailQtyBasisValueDisplay}
            setValue={setTrailQtyBasisValueDisplay}
            onBlur={(value) => {
              dispatchRatchetBracket({
                trailQtyBasisValue:
                  ratchetBracket.qtyBasis === 'QUANTITY'
                    ? value
                    : Number(value) / 100,
              });
            }}
          >
            {ratchetBracket.qtyBasis === 'QUANTITY' ? '#' : '%'}
          </NumberInputWithUnit>
        </div>
      </div>
    </div>
  );
}
RatchetBracketModule.propTypes = {
  state: PropTypes.array.isRequired,
};
export default RatchetBracketModule;
